import CodeMirror from 'codemirror'
import { consumeCustomFieldToken } from '../../features/custom-fields/editor-language'
import type { CustomFieldEditorContext } from '../../features/custom-fields/types'
import {
    isProperty,
    isMetric,
    isOperator,
    isDate,
    isRelativeDate,
    isNumber,
    isStringValue,
} from './syntax_common'

export const MERCHJAR_V2_MODE = 'merchjar-v2'
export const editableCustomFieldToken = consumeCustomFieldToken

export const addSyntaxV2 = () => {
    if (CodeMirror.modes[MERCHJAR_V2_MODE]) return

    CodeMirror.defineMode(MERCHJAR_V2_MODE, (_config, parserConfig) => {
        const customFieldContext = parserConfig?.customFieldContext as
            | CustomFieldEditorContext
            | undefined
        // Create a shared state object to track variables across all lines
        const sharedState = {
            variables: [],
            variableValues: {},
        }

        return {
            startState: () => ({
                tokenize: '',
                lastType: 'sof',
                variables: sharedState.variables,
                variableValues: sharedState.variableValues,
                isVariableDeclaration: false,
                isVariableValue: false,
                currentVariable: '',
                isComment: false,
                last: '',
                last_was_time_period: false,
            }),

            token: (stream, state) => {
                // Ensure variables and variableValues are initialized
                if (!state.variables) {
                    state.variables = sharedState.variables
                }
                if (!state.variableValues) {
                    state.variableValues = sharedState.variableValues
                }

                if (!state.isComment) {
                    const customFieldStyle = consumeCustomFieldToken(stream, customFieldContext)
                    if (customFieldStyle) return customFieldStyle
                }

                // Check if we're at the start of a variable identifier
                if (!state.isComment && !state.isVariableDeclaration && !state.isVariableValue) {
                    // Look ahead to see if the current position starts a variable name
                    const currentPos = stream.pos
                    const restOfLine = stream.string.slice(currentPos)

                    for (const varName of state.variables) {
                        if (
                            restOfLine.startsWith(varName) &&
                            (restOfLine.length === varName.length ||
                                !/[a-zA-Z0-9_]/.test(restOfLine[varName.length]))
                        ) {
                            // Found a variable at the current position
                            // Consume the variable name
                            for (let i = 0; i < varName.length; i++) {
                                stream.next()
                            }
                            return 'variable-v2'
                        }
                    }
                }

                const ch = stream.next()
                let results = null

                // Handle variable value collection
                if (state.isVariableValue) {
                    // If we see a semicolon, end the variable value collection
                    if (ch === ';') {
                        state.isVariableValue = false
                        state.isVariableDeclaration = false
                        return null
                    }

                    // Start collecting the value
                    let value = ch || ''

                    // Continue collecting until semicolon
                    let peek = stream.peek()
                    while (peek !== null && peek !== ';' && peek !== '\n') {
                        value += stream.next()
                        peek = stream.peek()
                    }

                    // Store the value for the current variable
                    if (state.currentVariable) {
                        const trimmedValue = value.trim()
                        state.variableValues[state.currentVariable] = trimmedValue
                        // Update the shared state
                        sharedState.variableValues[state.currentVariable] = trimmedValue

                        // Log for debugging
                        console.log('Variable value set:', state.currentVariable, trimmedValue)
                    }

                    // Return appropriate token type based on value content
                    if (/^\d+(\.\d+)?%?$/.test(value.trim()) || value.trim().startsWith('$')) {
                        return 'number-v2'
                    } else if (value.trim().startsWith('"') && value.trim().endsWith('"')) {
                        return 'string'
                    } else {
                        return 'number-v2' // Default to number for other values
                    }
                }

                if (!state.isComment && (ch === '(' || ch === ')')) {
                    return 'punctuation-v2'
                }
                if (!state.isComment && state.last_was_time_period && ch === ')') {
                    if (state.last === 'metric') {
                        return 'property-v2'
                    } else {
                        return 'property-v2'
                    }
                }

                // Functions
                if (!state.isComment && ch === 'c' && stream.match('ase')) {
                    return 'function-v2'
                } else if (!state.isComment && ch === 'n' && stream.match('ow')) {
                    return 'function-v2'
                } else if (!state.isComment && ch === 'i' && stream.match('nterval')) {
                    return 'function-v2'
                } else if (!state.isComment && ch === 't' && stream.match('o_timestamp')) {
                    return 'function-v2'
                }

                if (!state.isComment && ch === 'o' && stream.match('ver the')) {
                    return 'logic-v2'
                } else if (!state.isComment && ch === 'l' && stream.match('et')) {
                    return 'let-v2'
                } else if (!state.isComment && v2.isTimePeriod(ch, stream)) {
                    state.last_was_time_period = true
                    return 'time-period-v2'
                } else if (!state.isComment && isProperty(ch, stream)) {
                    state.last = 'property-v2'
                    return 'property-v2'
                } else if (!state.isComment && isMetric(window.isKdp, ch, stream)) {
                    state.last = 'property-v2'
                    return 'property-v2'
                } else if (!state.isComment && isOperator(ch, stream)) {
                    return 'operator-v2'
                } else if (!state.isComment && (isDate(ch, stream) || isRelativeDate(ch, stream))) {
                    return 'number-v2'
                } else if (!state.isComment && isNumber(ch, stream)) {
                    return 'number-v2'
                } else if (!state.isComment && ch === '"') {
                    if (stream.match(/.*?"/)) return 'time-period-v2'
                    stream.skipToEnd()
                    return 'string'
                } else if (!state.isComment && isStringValue(ch, stream)) {
                    return 'time-period-v2'
                } else if (!state.isComment && ch === 'a' && stream.match('nd')) {
                    return 'logic-v2'
                } else if (!state.isComment && ch === 'A' && stream.match('ND')) {
                    return 'logic-v2'
                } else if (!state.isComment && ch === 'o' && stream.match('r')) {
                    return 'logic-v2'
                } else if (!state.isComment && ch === 'O' && stream.match('R')) {
                    return 'logic-v2'
                } else if (!state.isComment && ch === 't' && stream.match('rue')) {
                    return 'logic-v2'
                } else if (!state.isComment && ch === 'T' && stream.match('RUE')) {
                    return 'logic-v2'
                } else if (!state.isComment && ch === 'f' && stream.match('alse')) {
                    return 'logic-v2'
                } else if (!state.isComment && ch === 'F' && stream.match('ALSE')) {
                    return 'logic-v2'
                } else if (!state.isComment && ch === 'w' && stream.match('hen')) {
                    return 'logic-v2'
                } else if (!state.isComment && ch === 't' && stream.match('hen')) {
                    return 'logic-v2'
                } else if (!state.isComment && ch === 'e' && stream.match('lse')) {
                    return 'logic-v2'
                } else if (!state.isComment && ch === '/') {
                    // inline comment
                    if (stream.match(/\/.*$/)) {
                        return 'comment-v2'
                    } else if (stream.match('*')) {
                        state.isComment = true
                        return 'comment-v2'
                    }
                } else if (state.isComment && ch == '*' && stream.match('/')) {
                    state.isComment = false
                    return 'comment-v2'
                } else if (state.isComment) {
                    return 'comment-v2'
                } else if (!state.isComment && ch === 'l' && stream.match('et')) {
                    state.isVariableDeclaration = true
                    return 'let-v2'
                } else if (!state.isComment && ch === '=' && state.isVariableDeclaration) {
                    state.isVariableValue = true
                    return 'operator-v2'
                } else if (!state.isComment && ch === ';') {
                    state.isVariableDeclaration = false
                    state.isVariableValue = false
                    return 'punctuation-v2'
                } else if (!state.isComment && ch === '-') {
                    return 'operator-v2'
                } else if (!state.isComment && ch === ')') {
                    return 'punctuation-v2'
                } else if (!state.isComment && ch === ',') {
                    return 'punctuation-v2'
                } else if (!state.isComment && (results = stream.match(/[$][a-zA-Z_0-9]+/))) {
                    const varName = results[0]
                    state.variables.push(varName)
                    state.currentVariable = varName

                    // Update the shared state
                    if (!sharedState.variables.includes(varName)) {
                        sharedState.variables.push(varName)
                    }

                    // Order variables by length in descending order.
                    // This helps to ensure that we match on the longest
                    // first. This ultimately prevents partial matches on
                    // the syntax highlighting.
                    state.variables.sort((a, b) => b.length - a.length)
                    sharedState.variables.sort((a, b) => b.length - a.length)

                    return null
                }
            },
        }
    })
}

const v2 = {
    isTimePeriod(ch: string | null, stream: CodeMirror.StringStream) {
        if (ch == 'l' && stream.match('ifetime')) {
            return true
        }

        // Check for the lifetime pattern (..)
        if (ch == '.' && stream.peek() === '.') {
            stream.next() // consume the second dot
            return true
        }

        // If we see a digit, look for single time period or range
        if (ch == '.' || (ch ?? '').match(/\d/)) {
            if (stream.match(/^\d*d\.\./)) {
                return true
            } else if (stream.match(/^\d*d/)) {
                return true
            } else if (stream.match(/^\d*d\.\.\d+d/)) {
                return true
            } else if (stream.match(/^\.\d+d/)) {
                return true
            }
        }

        // If we see an opening paren, look for the full pattern
        // if (ch === '(') {
        //     if (stream.match(/^\d*d\)/)) {
        //         // matches (4d)
        //         return true
        //     }
        //     if (stream.match(/^\d+d\.\.\d+d\)/)) {
        //         return true
        //     }
        // }

        return false
    },
}
